// Online play: a shared room, one game state, everybody watching it.
//
// Ported from no-thanks, which took it from thunderstone: client-authoritative
// full-snapshot sync over Firebase RTDB, every move through the same
// engine.dispatch() as a local game.
//
// WHAT IS DIFFERENT HERE, and it is the whole shape of the file: Incan Gold has
// no turns. Everybody decides at once, every turn. So there is no
// `activePlayerIndex` to ask "is it my go?" about - instead every player writes
// their own secret choice, and one client (the driver) notices when the last
// one has landed and plays the turn out.
//
// A NOTE ON SECRECY, because the word "secret" above is doing less work than it
// sounds like. The whole game state lives in one JSON blob that every client in
// the room can read, so a player with devtools open could see what everyone else
// chose before the reveal - and, for that matter, read the shuffled deck. That
// is true of every game in this family (no-thanks stores its deck the same way)
// and it is not fixable without moving the engine onto a server, which is a
// different project. These are games for a family who trust each other; the
// honest description is "hidden from the interface", not "cryptographically
// secret", and pretending otherwise in the code would be worse than saying so.

import { deadAfterFor, waitingPatch } from './asyncPlay.js';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { createGameState } from '../engine/state/createGameState';
import { PHASES, MIN_PLAYERS, MAX_PLAYERS, CHOICES } from '../engine/constants';
import { PERSONALITIES, personalityFor } from '../engine/ai/heuristicBot';

export const GAMES = 'incanGoldGames';
export const CODES = 'incanGoldGameCodes';
export const USER_GAMES = 'incanGoldUserGames';

/**
 * How long a room with nobody in it may sit before it is cleaned up.
 *
 * Presence is the real signal - Firebase clears it the instant a connection
 * drops - and these are the safety margin on top. A lobby holds nothing, so it
 * goes quickly; a game in progress gets a full day, because "we'll finish this
 * tomorrow" is a real thing people do.
 */
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  'in-progress': 24 * 60 * 60 * 1000,
  finished: 2 * 60 * 60 * 1000,
};

/**
 * How long a player can be gone before the table stops waiting for them.
 *
 * Matt's call: nobody gets a countdown clock on their decision, but one closed
 * laptop must not hold five people hostage for an hour. After this, the driver
 * decides for them - and decides that they LEFT, which banks whatever they were
 * carrying rather than risking it on cards they aren't there to see. Walking
 * away from Incan Gold should cost you the rest of the round, not your pocket.
 */
export const DISCONNECT_GRACE_MS = 30000;

/** Best-effort "when was anyone last here", tolerant of older rooms. */
export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Two conditions, both required. Anyone connected always saves a room. */
export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  const age = now - lastActiveOf(meta);
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return age > limit;
};

export const clampTableSize = (n) => Math.min(Math.max(Number(n) || MIN_PLAYERS, MIN_PLAYERS), MAX_PLAYERS);

/**
 * The state is stored as a JSON STRING, not as a tree.
 *
 * RTDB has no representation for an empty array or object and silently drops
 * the key. `path: []`, `artifactsOnPath: []` and `hazardsSeen: {}` are all
 * empty at the start of every round, so a tree write loses them and the state
 * comes back subtly broken. Stringifying keeps it byte-identical.
 */
const encode = (state) => JSON.stringify(state);
const decode = (raw) => (raw ? JSON.parse(raw) : null);

export const seqOf = (state) => state?.meta?.seq ?? 0;

/**
 * Who is allowed to write on behalf of the table.
 *
 * The connected human in the lowest seat drives. Derived purely from state
 * every client already has, so they all agree without negotiating, and it
 * survives the host closing their laptop - which a "host drives" rule would
 * not. Two clients briefly disagreeing about who is connected is harmless:
 * both would write the same decision for the same seat, and the second write
 * finds the seat already decided and does nothing.
 */
export const driverUidFor = (gameState, presence) => {
  const connected = (uid) => Boolean(presence?.[uid]);
  const humans = (gameState?.players || []).filter((p) => !p.isBot && connected(p.id));
  if (humans.length === 0) return null;
  return humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
};

/**
 * Is this seat one that nobody is going to decide for themselves?
 *
 * True for a bot, and for a human whose connection has been gone longer than
 * the grace period. False for a connected human - including you.
 */
export const seatIsUnattended = (player, presence, disconnectedSince, asyncRoom = false) => {
  if (player.isBot) return true;
  // Async play (asyncPlay.js): a human who is not here is not here yet. The game waits.
  if (asyncRoom) return false;
  if (presence?.[player.id]) return false;
  const since = disconnectedSince?.[player.id];
  return Boolean(since) && Date.now() - since >= DISCONNECT_GRACE_MS;
};

/**
 * Which seats THIS client owes a decision for right now.
 *
 * The two halves are separate questions and conflating them is what broke the
 * first online game of No Thanks! - a single "am I responsible for this seat"
 * predicate is true for your own seat too, and the caller read any true as
 * licence to play automatically, so every client cheerfully bot-played its own
 * human. Here: a seat has to be unattended AND this client has to be the
 * driver. Your own seat is never decided for you by anybody.
 */
export const seatsIOwe = ({ gameState, presence, myUid, disconnectedSince, asyncRoom = false }) => {
  if (!gameState || gameState.meta.phase === PHASES.FINISHED || gameState.meta.roundOver) return [];
  if (driverUidFor(gameState, presence) !== myUid) return [];
  return gameState.players.filter((p) => (
    p.inTemple
    && p.choice === null
    && seatIsUnattended(p, presence, disconnectedSince, asyncRoom)
  ));
};

/**
 * What an unattended seat does. Always leave - see DISCONNECT_GRACE_MS.
 * A bot gets a real decision instead; this is only for absent humans.
 */
export const choiceForAbsentSeat = () => CHOICES.LEAVE;

/** Purely a UI question: are the buttons mine to press? Never authorises a write. */
export const iNeedToDecide = ({ gameState, myUid }) => {
  if (!gameState || gameState.meta.phase === PHASES.FINISHED || gameState.meta.roundOver) return false;
  const me = gameState.players.find((p) => p.id === myUid);
  return Boolean(me && me.inTemple && me.choice === null);
};

/**
 * Which bot temperament plays a given seat. Real bot seats keep the one they
 * were dealt; a stand-in never happens here (an absent human leaves rather
 * than being played for), so this only ever answers for bots.
 */
export const personalityForSeat = (player) => personalityFor(player.botIndex ?? 0);

/**
 * Incan Gold's async adapter (asyncPlay.js): the expedition waits on everyone
 * still in the temple who has not chosen. A bot never waits on itself; it
 * waits on the lowest-seat human, who drives the bots when they open the room.
 */
const lowestHuman = (g) => {
  const humans = (g?.players || []).filter((p) => !p.isBot);
  return humans.length ? [humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id] : [];
};
export const asyncAdapter = {
  waitingOnOf: (g) => {
    if (!g || g.meta.phase === PHASES.FINISHED) return [];
    if (g.meta.roundOver) return lowestHuman(g);
    const deciding = g.players.filter((p) => p.inTemple && p.choice === null && !p.isBot).map((p) => p.id);
    return deciding.length ? deciding : (g.players.some((p) => p.inTemple && p.choice === null) ? lowestHuman(g) : []);
  },
  isOver: (g) => g?.meta?.phase === PHASES.FINISHED,
};

/** After a move in an async room: who the game waits on now (asyncPlay.js). */
export const recordWaiting = async ({ gameId, gameState, meta }) => {
  if (!db || !meta?.async || !gameState) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export const createRoom = async ({ user, tableSize = MIN_PLAYERS, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    roomCode: code,
    async: asyncRoom,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    tableSize: clampTableSize(tableSize),
    playerSlots: [{
      uid: user.uid,
      displayName: user.displayName || 'Host',
      seatIndex: 0,
    }],
  };

  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });

  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();

  const slots = meta.playerSlots || [];
  const alreadyIn = slots.some((s) => s.uid === user.uid);

  // Rejoining a game you already have a seat in is always allowed, even
  // mid-game - that IS the path back for someone who dropped out.
  if (!alreadyIn) {
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    if (slots.length >= (meta.tableSize || MAX_PLAYERS)) throw new Error('That game is full.');
    slots.push({
      uid: user.uid,
      displayName: user.displayName || 'Guest',
      seatIndex: slots.length,
    });
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }

  return { gameId };
};

export const setTableSize = async ({ user, gameId, meta, tableSize }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can change the table size.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const seated = (meta.playerSlots || []).length;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    tableSize: clampTableSize(Math.max(tableSize, seated)),
  });
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return; // mid-game seats are kept, not vacated
  const slots = (meta.playerSlots || [])
    .filter((s) => s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

/**
 * Host-only: turns the lobby's seats into a real game. Bots top the table up
 * to the host's chosen size, so two friends can start without waiting for a
 * third - which matters more here than anywhere else, since the rulebook's
 * minimum is three.
 */
export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can set off.');

  const seats = [...(meta.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex);
  if (seats.length === 0) throw new Error('Nobody is in the room.');

  const humans = seats.map((s) => ({ id: s.uid, name: s.displayName, isBot: false }));
  const total = clampTableSize(Math.max(meta.tableSize || MIN_PLAYERS, humans.length));
  const bots = Array.from({ length: Math.max(0, total - humans.length) }, (_, i) => ({
    id: `bot${i}`,
    name: PERSONALITIES[personalityFor(i)].label,
    isBot: true,
    botIndex: i,
  }));

  // Host-authoritative seed, so every client renders the identical game from
  // the snapshot it receives rather than each shuffling its own.
  const seed = Math.floor(Math.random() * 2 ** 31);
  const gameState = createGameState({ players: [...humans, ...bots], seed });

  await set(dbRef(db, `${GAMES}/${gameId}/state`), encode(gameState));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'in-progress',
    lastActiveAt: Date.now(),
    // An async room says who it waits on from the first turn (asyncPlay.js).
    ...(meta.async ? waitingPatch(gameState, meta, asyncAdapter) : {}),
  });
  return gameState;
};

/**
 * Applies one action to the shared state, inside a transaction.
 *
 * NO OPTIMISTIC-CONCURRENCY TOKEN, deliberately, and this is the one place
 * that differs from no-thanks in a way that matters.
 *
 * That game's version captured `meta.seq` before the write and aborted if it
 * had moved. It could afford to: only one player acts at a time, so a
 * collision is rare and a dropped write is just a re-tap. Here EVERY player
 * writes on EVERY turn, in the same second, to this same node - so with a seq
 * check the first tap wins and every other one aborts and is silently thrown
 * away. The people who tapped stay showing as "thinking…" and have to tap
 * again, with no idea why.
 *
 * It bought nothing, either. A transaction handler already re-runs against
 * whatever is actually stored, so there is no stale read to defend against,
 * and every action in this engine guards itself against the fresh state:
 * DECIDE does nothing for a seat that has already chosen, RESOLVE_TURN does
 * nothing unless everybody has, and START_ROUND does nothing unless a round
 * is actually over. Each of those returns the state unchanged, which shows up
 * as an unmoved `seq` and aborts the write - which is exactly the protection
 * the seq check was there to provide, applied at the point that can actually
 * tell.
 *
 * `apply` returns null (or an unchanged state) to abandon.
 */
export const commitAction = async ({ gameId, apply }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const result = await runTransaction(dbRef(db, `${GAMES}/${gameId}/state`), (raw) => {
    const current = decode(raw);
    if (!current) return raw;
    const next = apply(current);
    // Returning undefined aborts and does NOT retry, which is what we want
    // for an action that turned out to be a no-op against fresh data.
    if (!next || seqOf(next) === seqOf(current)) return undefined;
    return encode(next);
  });
  return { committed: result.committed, state: decode(result.snapshot.val()) };
};

/**
 * Deletes the caller's own dead rooms.
 *
 * Self-service rather than a global sweep, because that is what the security
 * rules permit: `.read` is granted per room and NOT on the collection, so no
 * client can list every room, and deleting one is the host's alone. Everyone
 * tidying up after themselves covers the whole database without anyone seeing
 * anyone else's games. Entirely best-effort - nothing about starting a game
 * should ever wait on housekeeping.
 */
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;

  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop -- serial keeps one failure
      // from cancelling the rest.
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;

      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;

      // Room first: a code may only be released once it points at nothing.
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // A permission change, a dropped connection - none of it should stop
      // the rest of the sweep.
    }
  }
  return { deleted, pruned };
};

export const bindRoom = ({ gameId, user, onMeta, onState, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');

  const metaRef = dbRef(db, `${GAMES}/${gameId}/meta`);
  const stateRef = dbRef(db, `${GAMES}/${gameId}/state`);
  const presenceRef = dbRef(db, `${GAMES}/${gameId}/presence`);

  const offMeta = onValue(metaRef, (snap) => onMeta(snap.val()));
  const offState = onValue(stateRef, (snap) => onState(decode(snap.val())));
  const offPresence = onValue(presenceRef, (snap) => onPresence(snap.val() || {}));

  // Let Firebase clear our presence the instant the connection drops - a
  // closed tab, a dead network, a crashed browser. Far more reliable than an
  // explicit "I'm leaving" write, which a force-quit never gets to send.
  let myPresenceRef = null;
  if (user) {
    myPresenceRef = dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offMeta();
    offState();
    offPresence();
    if (myPresenceRef) remove(myPresenceRef);
  };
};
